import { useCallback, useState } from "react";
import { toast } from "sonner";

// useStudioSubjectLock — Studio's local wrapper for the session-scoped
// "subject lock" (stored in sessionStorage). Separate from the read-only
// `useSubjectLock` hook other pages use, because Studio owns the writes
// AND tracks a "cleared" flag that mutes auto-lock re-arm until the user
// takes a fresh action (new upload / new prompt submit).
//
// Extracted from Studio.jsx as-is — no behavior change. Setters are
// memoized so hook consumers can safely include them in effect deps.
const SUBJECT_LOCK_KEY = "geneo:subjectLock";
const SUBJECT_LOCK_CLEARED_KEY = "geneo:subjectLockCleared";

export function useStudioSubjectLock() {
  const [subjectLock, setSubjectLockRaw] = useState(() => {
    try {
      const raw = sessionStorage.getItem(SUBJECT_LOCK_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [subjectLockCleared, setSubjectLockCleared] = useState(() => {
    try { return sessionStorage.getItem(SUBJECT_LOCK_CLEARED_KEY) === "1"; } catch { return false; }
  });

  const setSubjectLock = useCallback((next) => {
    setSubjectLockRaw(next);
    try {
      if (next) {
        sessionStorage.setItem(SUBJECT_LOCK_KEY, JSON.stringify(next));
        sessionStorage.removeItem(SUBJECT_LOCK_CLEARED_KEY);
        setSubjectLockCleared(false);
      } else {
        sessionStorage.removeItem(SUBJECT_LOCK_KEY);
      }
    } catch {}
    window.dispatchEvent(new CustomEvent("geneo:subjectLockChanged"));
  }, []);

  const clearSubjectLock = useCallback(() => {
    setSubjectLock(null);
    setSubjectLockCleared(true);
    try { sessionStorage.setItem(SUBJECT_LOCK_CLEARED_KEY, "1"); } catch {}
    window.dispatchEvent(new CustomEvent("geneo:subjectLockChanged"));
    toast.success("Subject unlocked — next generation starts fresh.");
  }, [setSubjectLock]);

  // Any fresh explicit user action (new upload, new prompt submit) re-arms auto-lock.
  const rearmSubjectLock = useCallback(() => {
    setSubjectLockCleared((wasCleared) => {
      if (wasCleared) {
        try { sessionStorage.removeItem(SUBJECT_LOCK_CLEARED_KEY); } catch {}
        return false;
      }
      return wasCleared;
    });
  }, []);

  return {
    subjectLock,
    subjectLockCleared,
    setSubjectLock,
    clearSubjectLock,
    rearmSubjectLock,
  };
}
